"use client";

import { Search } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { ProductsGrid } from "@/features/products/ui/components/ProductsGrid";
import { usePathname } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { NotFound } from "@/lib/client/components/NotFound";
import { useDisclosure } from "@/lib/shared/hooks";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/ui";
import { useSearchProducts } from "../hooks/useSearchProducts";
import { Loading } from "./Loading";
import { SearchBox } from "./SearchBox";

const DEBOUNCE_MS = 1000;

/**
 * SearchBar component - Product search functionality
 */
export const SearchBar = () => {
  const navigationT = useTranslations("navigation");
  const pathName = usePathname();
  const { isOpen, toggle } = useDisclosure();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const prevPathnameRef = useRef(pathName);

  const { data: result, isFetching } = useSearchProducts(debouncedQuery);

  useEffect(() => {
    if (prevPathnameRef.current !== pathName) {
      prevPathnameRef.current = pathName;
      if (isOpen) {
        toggle(false);
      }
    }
  }, [pathName, isOpen, toggle]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const query = searchValue.trim();
      setDebouncedQuery(query);
      // Track search performed when query is not empty
      if (query) {
        trackEvent(AnalyticsEvents.SEARCH_PERFORMED, {
          query,
          query_length: query.length,
        });
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      trackEvent(AnalyticsEvents.SEARCH_OPENED);
    } else {
      trackEvent(AnalyticsEvents.SEARCH_CLOSED);
    }
    toggle(open);
  };

  const handleProductClick = () => {
    trackEvent(AnalyticsEvents.SEARCH_RESULT_CLICKED, {
      query: debouncedQuery,
      result_count: result?.length || 0,
    });
    toggle(false);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="join relative h-auto w-full items-center justify-between gap-0 overflow-hidden rounded-md border-0 bg-transparent p-0 shadow-none hover:bg-transparent"
          data-testid="product-search-trigger"
        >
          <span className="join-item flex-1 truncate rounded-l-md border border-input bg-transparent px-3 py-2 text-left text-sm text-muted-foreground sm:text-sm">
            {navigationT("search")}
          </span>
          <span
            aria-hidden="true"
            className="join-item inline-flex items-center justify-center rounded-r-md border border-l-0 border-input px-3 py-2"
          >
            <Search className="h-6 w-6" />
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-6xl px-6 md:max-w-3xl lg:px-8 h-[60vh] flex flex-col"
        data-testid="product-search-dialog"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="my-2">
          <DialogTitle>
            <SearchBox
              value={searchValue}
              onValueChange={setSearchValue}
            />
          </DialogTitle>
        </DialogHeader>
        {debouncedQuery ? (
          isFetching ? (
            <Loading />
          ) : result && result.length > 0 ? (
            <ProductsGrid
              products={result}
              className="overflow-y-auto overflow-x-hidden"
              onProductClick={handleProductClick}
            />
          ) : (
            <NotFound className="flex-1 items-center justify-center" />
          )
        ) : (
          <NotFound className="flex-1 items-center justify-center" />
        )}
      </DialogContent>
    </Dialog>
  );
};
